// Ported from dev-scrips-pm-react src/billing/components/invoice-pending-orders.tsx (PROD-3312 appointment Billing tab,
// step 6). PM primitives come from the PROD-3310 billing bridge. Differences from pm-react:
//   - PM's `text-[length:var(--type-*-size)] leading-[var(--type-*-line)]` arbitrary classes are the named utilities on
//     the same tokens: body 13/20 -> `text-sm leading-5`, label 12/16 -> `text-xs leading-4`, meta 12 -> `text-xs`. The
//     section heading uses `--type-section-size`, which pm-react never defines, so it inherits the body size there; it
//     carries no size class here (same as the page's own "Payments" heading).
//   - `text-[var(--color-brand-depth)]` is the named `text-brand-depth`.
import { useMemo, useState } from 'react';
import { AlertCircle, Plus } from 'lucide-react';
import { toast } from 'sonner';

import { useAuthStore } from '@/auth/auth-store';
import { BridgeButton as Button, BridgeCheckbox as Checkbox, BridgeStatusChip, Skeleton } from '@/billing/bridge';
import { isSelfPay as isSelfPayType } from '@/billing/lib/sponsor-type';
import {
  addItemsFor,
  addItemsWithPickedService,
  groupFee,
  groupOrderLines,
  groupReadiness,
  hasAnyOrders,
  itemRefsFor,
  type OrderGroup,
} from '@/billing/lib/pending-orders';
import { useInvoice } from '@/billing/services/use-invoice';
import {
  useAddOrders,
  useDismissOrders,
  useInvoiceOrders,
  useRestoreOrders,
} from '@/billing/services/use-invoice-orders';
import { formatMoney } from '@/shared/lib/money';
import { DismissedOrders } from './invoice-dismissed-orders';
import { PendingOrderRow } from './invoice-pending-order-row';
import { ServicePickerDialog } from './service-picker-dialog';
import { AddOrdersConfirmDialog, DismissOrdersPopover, type DismissChoice } from './invoice-pending-orders-dialogs';

interface Props {
  invoiceId: string;
  /** The host invoice is a Draft. Adding, dismissing and restoring need it. */
  isDraft: boolean;
  currency: string;
}

/**
 * Order to Invoice (PROD-2591): the "Pending orders" section of an invoice.
 *
 * Orders from the encounter's Assessment & Plan, with an indicative price, that
 * are NOT on the invoice. Manual only: a biller adds each one (after a confirm,
 * since it moves money into Patient Payable) or dismisses it (not billed here;
 * the clinical order is not cancelled). Dismissed orders can be restored.
 *
 * Self-contained: it reads and mutates through `use-invoice-orders.ts` and
 * never touches the invoice totals. Hidden for device bookings
 * (`state: "NoEncounter"`) and when the plan has no orders.
 */
export function InvoicePendingOrders({ invoiceId, isDraft, currency }: Props) {
  // Every hook sits above the early returns: the first render is a loading one,
  // and a hook below a return changes the hook count on the next render.
  const ordersQuery = useInvoiceOrders(invoiceId);
  const invoiceQuery = useInvoice({ invoiceId });
  const user = useAuthStore((s) => s.user);
  const addOrders = useAddOrders();
  const dismissOrders = useDismissOrders();
  const restoreOrders = useRestoreOrders();
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [confirm, setConfirm] = useState<OrderGroup[] | null>(null);
  // PROD-2678: the NoPrice order whose service is being picked.
  const [pickFor, setPickFor] = useState<OrderGroup | null>(null);

  const data = ordersQuery.data;
  const pendingGroups = useMemo(() => groupOrderLines(data?.pending ?? []), [data]);
  const dismissedGroups = useMemo(() => groupOrderLines(data?.dismissed ?? []), [data]);

  if (ordersQuery.isLoading) return <PendingOrdersSkeleton />;
  if (!hasAnyOrders(data)) return null;

  const editable = isDraft && data.canEdit;
  // Read-only (Issued / Cancelled) with nothing pending: nothing to act on.
  if (!editable && pendingGroups.length === 0) return null;

  const busy = addOrders.isPending || dismissOrders.isPending || restoreOrders.isPending;
  // Derived, not stored: a selected row that left the list simply drops out of
  // the selection. PROD-2678: any pending row can be ticked (Dismiss takes them
  // all); Add and the indicative total take only the ready ones.
  const selectedGroups = pendingGroups.filter((g) => selected.has(g.key));
  const addableGroups = selectedGroups.filter((g) => groupReadiness(g, choices) === 'ready');
  const allSelected = pendingGroups.length > 0 && selectedGroups.length === pendingGroups.length;
  const selectedAmount = addableGroups.reduce((sum, g) => sum + (groupFee(g, choices) ?? 0), 0);
  const partialAdd = addableGroups.length < selectedGroups.length;
  const userName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'you';

  const toggle = (key: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  const clearSelection = () => setSelected(new Set());
  const removeFromSelection = (groups: OrderGroup[]) =>
    setSelected((prev) => {
      const next = new Set(prev);
      groups.forEach((g) => next.delete(g.key));
      return next;
    });

  const confirmAdd = () => {
    if (!confirm) return;
    const groups = confirm;
    addOrders.mutate(
      { invoiceId, request: { items: addItemsFor(groups, choices) } },
      {
        onSuccess: (res) => {
          const title =
            groups.length === 1
              ? `${groups[0].primary.display} added to the invoice`
              : `${groups.length} orders added to the invoice`;
          const payable = res?.invoice?.patientPayable;
          toast.success(title, {
            description: payable !== undefined ? `Patient Payable ${currency} ${formatMoney(payable)}` : undefined,
          });
          removeFromSelection(groups);
          setConfirm(null);
        },
        // The hook toasts the error; close so the refetched list is visible.
        onError: () => setConfirm(null),
      },
    );
  };

  // NoPrice: the picker's choice is the confirmation; the picked service rides
  // as `serviceId` (PROD-2594).
  const addWithPickedService = (group: OrderGroup, serviceId: string) =>
    addOrders.mutate(
      { invoiceId, request: { items: addItemsWithPickedService(group, choices, serviceId) } },
      {
        onSuccess: (res) => {
          const payable = res?.invoice?.patientPayable;
          toast.success(`${group.primary.display} added to the invoice`, {
            description: payable !== undefined ? `Patient Payable ${currency} ${formatMoney(payable)}` : undefined,
          });
          removeFromSelection([group]);
        },
      },
    );

  const dismiss = async (groups: OrderGroup[], choice: DismissChoice): Promise<boolean> => {
    try {
      await dismissOrders.mutateAsync({
        invoiceId,
        request: { items: itemRefsFor(groups), reason: choice.reason, note: choice.note },
      });
      toast.success(
        groups.length === 1
          ? `${groups[0].primary.display} dismissed from this invoice`
          : `${groups.length} orders dismissed from this invoice`,
      );
      removeFromSelection(groups);
      return true;
    } catch {
      return false; // the hook toasts the server message
    }
  };

  const restore = (group: OrderGroup) =>
    restoreOrders.mutate(
      { invoiceId, request: { items: itemRefsFor([group]) } },
      { onSuccess: () => toast.success(`${group.primary.display} restored to pending orders`) },
    );

  const invoice = invoiceQuery.data;

  return (
    <section
      aria-labelledby="pending-orders-heading"
      className="@container rounded-xl border border-border-default bg-surface-card px-6 py-6 flex flex-col gap-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 id="pending-orders-heading" className="font-semibold text-text-primary">
            Pending orders
          </h3>
          {pendingGroups.length > 0 && (
            <BridgeStatusChip semantic="neutral" tone="soft" size="xs">
              {pendingGroups.length}
            </BridgeStatusChip>
          )}
        </div>
        <span className="text-xs leading-5 text-text-muted">From Assessment &amp; Plan</span>
      </div>

      {!editable && (
        <div className="flex items-start gap-2 rounded-xl border border-border-default bg-grey-100 px-4 py-3 text-sm leading-5 text-text-secondary">
          <AlertCircle className="mt-1 size-4 shrink-0" aria-hidden />
          <span>Revert to draft to add orders.</span>
        </div>
      )}

      {pendingGroups.length === 0 ? (
        <p className="py-2 text-sm leading-5 text-text-muted">
          No pending orders. Orders placed in Assessment &amp; Plan appear here with an indicative price.
        </p>
      ) : (
        <>
          <p className="-mt-2 text-xs leading-5 text-text-muted">
            Indicative prices. Not part of Patient Payable until someone adds them to the invoice. Dismiss leaves an
            order off this invoice without cancelling it.
          </p>

          {editable && selectedGroups.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-xl bg-blue-100 py-1.5 pr-2 pl-3">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm leading-5">
                <span className="font-medium text-brand-depth tabular-nums">{selectedGroups.length} selected</span>
                <span className="text-text-secondary tabular-nums">
                  {currency} {formatMoney(selectedAmount)} indicative
                </span>
                <button type="button" onClick={clearSelection} className="text-sm text-brand-primary hover:underline">
                  Clear
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <DismissOrdersPopover
                  title={
                    selectedGroups.length === 1
                      ? `Dismiss ${selectedGroups[0].primary.display} from this invoice?`
                      : `Dismiss ${selectedGroups.length} orders from this invoice?`
                  }
                  pending={busy}
                  onConfirm={(choice) => dismiss(selectedGroups, choice)}
                  trigger={
                    <Button variant="outline" size="sm" disabled={busy} className="bg-surface-card text-text-secondary">
                      Dismiss selected
                    </Button>
                  }
                />
                <Button size="sm" disabled={busy || addableGroups.length === 0} onClick={() => setConfirm(addableGroups)}>
                  <Plus aria-hidden />{' '}
                  {partialAdd
                    ? `Add ${addableGroups.length} of ${selectedGroups.length} to invoice`
                    : 'Add selected to invoice'}
                </Button>
              </div>
            </div>
          )}

          <table className="w-full text-left text-sm leading-5">
            <thead className="border-b border-border-default text-xs font-medium leading-4 text-text-secondary">
              <tr>
                {editable && (
                  <th className="w-4 px-3 py-2 font-medium">
                    <Checkbox
                      aria-label="Select all pending orders"
                      checked={allSelected}
                      disabled={busy}
                      onCheckedChange={(v) => setSelected(v ? new Set(pendingGroups.map((g) => g.key)) : new Set())}
                    />
                  </th>
                )}
                <th className="px-3 py-2 font-medium">Order</th>
                <th className="hidden px-3 py-2 font-medium @xl:table-cell">Type</th>
                <th className="hidden px-3 py-2 font-medium @3xl:table-cell">Order status</th>
                <th className="hidden px-3 py-2 font-medium @3xl:table-cell whitespace-nowrap">Ordered by</th>
                <th className="px-3 py-2 text-right font-medium whitespace-nowrap">Indicative, {currency}</th>
                {editable && <th className="px-3 py-2" aria-label="actions" />}
              </tr>
            </thead>
            <tbody>
              {pendingGroups.map((g) => (
                <PendingOrderRow
                  key={g.key}
                  group={g}
                  choices={choices}
                  editable={editable}
                  busy={busy}
                  selected={selected.has(g.key)}
                  onToggle={toggle}
                  onChoose={(key, serviceId) => setChoices((prev) => ({ ...prev, [key]: serviceId }))}
                  onAdd={(group) => setConfirm([group])}
                  onPickService={setPickFor}
                  onDismiss={(group, choice) => dismiss([group], choice)}
                />
              ))}
            </tbody>
          </table>
        </>
      )}

      {editable && (
        <DismissedOrders
          groups={dismissedGroups}
          currency={currency}
          editable={editable}
          busy={busy}
          onRestore={restore}
        />
      )}

      <AddOrdersConfirmDialog
        target={
          confirm
            ? {
                names: confirm.map((g) => g.primary.display),
                amount: confirm.reduce((sum, g) => sum + (groupFee(g, choices) ?? 0), 0),
              }
            : null
        }
        currency={currency}
        patientPayable={invoice?.patientPayable}
        selfPay={isSelfPayType(invoice?.sponsorType)}
        userName={userName}
        pending={addOrders.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={confirmAdd}
      />

      {pickFor && invoice && (
        // Remounted per order so the search is seeded fresh with its code.
        <ServicePickerDialog
          key={pickFor.key}
          open
          onOpenChange={(open) => !open && setPickFor(null)}
          initialQuery={pickFor.primary.code}
          providerId={invoice.providerId ?? invoice.provider?.id}
          locationId={invoice.locationId}
          patientId={invoice.patientId ?? invoice.patient?.id}
          currency={currency}
          existingServiceIds={(invoice.services ?? [])
            .map((s) => s.serviceId)
            .filter((id): id is string => Boolean(id))}
          onSelect={(serviceId) => addWithPickedService(pickFor, serviceId)}
        />
      )}
    </section>
  );
}

function PendingOrdersSkeleton() {
  return (
    <section
      aria-label="Loading pending orders"
      className="rounded-xl border border-border-default bg-surface-card px-6 py-6 flex flex-col gap-3"
    >
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
    </section>
  );
}
